import { C_TEAL, rgba } from '../utils/palette';
import { setupCanvas } from '../utils/canvas';

export const CHART_KEY = [
    'X = position in ranking · Y = total ranking points',
    'Curve shows how sharply points drop off down the ranking',
    'Tap for rider name and exact points at that position',
];

/**
 * Line + area chart of points by position.
 * @param {HTMLCanvasElement} canvas
 * @param {{ position: number, points: number }[]} riders  sorted by position asc
 * @param {number} [clipPct]  show top N% of riders (100 = all)
 * @param {HTMLElement} [tooltip]
 */
export function renderPointsDistribution(canvas, riders, clipPct = 100, tooltip = null) {
    if (!canvas || riders.length < 2) return;
    riders = riders.slice(0, Math.max(2, Math.ceil(riders.length * (clipPct / 100))));
    const pad   = { top: 12, right: 12, bottom: 24, left: 38 };
    const w     = canvas.parentElement.clientWidth;
    const h     = 110;
    const ctx   = setupCanvas(canvas, w, h);
    ctx.clearRect(0, 0, w, h);

    const maxPts = riders[0].points;
    const maxPos = riders[riders.length - 1].position;
    const minPos = riders[0].position;
    const iw     = w - pad.left - pad.right;
    const ih     = h - pad.top - pad.bottom;
    const px     = pos => pad.left + ((pos - minPos) / (maxPos - minPos || 1)) * iw;
    const py     = pts => pad.top + ih - (pts / maxPts) * ih;

    // area
    ctx.beginPath();
    riders.forEach((r, i) => i === 0 ? ctx.moveTo(px(r.position), py(r.points)) : ctx.lineTo(px(r.position), py(r.points)));
    ctx.lineTo(px(maxPos), pad.top + ih);
    ctx.lineTo(px(minPos), pad.top + ih);
    ctx.closePath();
    ctx.fillStyle = rgba(C_TEAL, 0.08);
    ctx.fill();

    // line
    ctx.beginPath();
    riders.forEach((r, i) => i === 0 ? ctx.moveTo(px(r.position), py(r.points)) : ctx.lineTo(px(r.position), py(r.points)));
    ctx.strokeStyle = rgba(C_TEAL, 0.6);
    ctx.lineWidth   = 1.5;
    ctx.lineJoin    = 'round';
    ctx.stroke();

    // dots + hit areas
    const dots = [];
    riders.forEach(r => {
        const cx = px(r.position);
        const cy = py(r.points);
        ctx.beginPath();
        ctx.arc(cx, cy, 2, 0, 2 * Math.PI);
        ctx.fillStyle = rgba(C_TEAL, 0.5);
        ctx.fill();
        dots.push({ cx, cy, pos: r.position, pts: r.points });
    });

    // axis labels
    ctx.fillStyle    = '#a3a3a3';
    ctx.font         = '9px monospace';
    ctx.textAlign    = 'right';
    ctx.textBaseline = 'middle';
    ctx.fillText(Math.round(maxPts),     pad.left - 4, py(maxPts));
    ctx.fillText(Math.round(maxPts / 2), pad.left - 4, py(maxPts / 2));
    ctx.textAlign    = 'center';
    ctx.textBaseline = 'top';
    const step = Math.max(1, Math.floor(riders.length / 5));
    riders.forEach((r, i) => {
        if (i % step !== 0 && i !== riders.length - 1) return;
        ctx.fillText(r.position, px(r.position), h - pad.bottom + 4);
    });

    if (!tooltip) return;
    if (canvas._ptsMM) canvas.removeEventListener('mousemove',  canvas._ptsMM);
    if (canvas._ptsML) canvas.removeEventListener('mouseleave', canvas._ptsML);
    canvas._ptsMM = (e) => {
        const rect = canvas.getBoundingClientRect();
        const mx   = e.clientX - rect.left;
        const my   = e.clientY - rect.top;
        const hit  = dots.find(d => Math.hypot(d.cx - mx, d.cy - my) <= 6);
        if (hit) {
            tooltip.textContent   = `#${hit.pos} · ${hit.pts.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} pts`;
            tooltip.style.display = 'block';
            const _tw1 = tooltip.offsetWidth;
            tooltip.style.left    = (mx + 10 + _tw1 > canvas.parentElement.clientWidth ? mx - _tw1 - 10 : mx + 10) + 'px';
            tooltip.style.top     = (my - 24) + 'px';
        } else {
            tooltip.style.display = 'none';
        }
    };
    canvas._ptsML = () => { tooltip.style.display = 'none'; };
    canvas.addEventListener('mousemove',  canvas._ptsMM);
    canvas.addEventListener('mouseleave', canvas._ptsML);
}
